import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StyleSheet, css } from 'aphrodite';
import { motion } from 'framer-motion';
import * as Yup from 'yup';
import { showToast, verifyRecaptcha } from '../../../utils/config/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { loginHospital } from '../../../redux/middleware/user/hospital/login';
import { hospitalVerifyEmail } from '../../../redux/middleware/user/hospital/verification';
import { CircularProgress } from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { GoogleLogin } from '@react-oauth/google';
import { Eye, EyeOff } from 'react-feather';
import Divider from '../../../components/hospital/misc/Divider';
import { APP_URLS } from '../../../../urls';
import { toast } from 'react-toastify';
import useGoogleAuth from '../../../hooks/useGoogleAuth';

const validationSchema = Yup.object().shape({
	email: Yup.string().email('Invalid email').required('Email is required'),
	password: Yup.string().required('Password is required'),
});

const HospitalLogin = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const captchaRef = useRef(null);
	const [captchaValue, setCaptchaValue] = useState(null);
	const [captchaError, setCaptchaError] = useState('');
	const [successMessage, setSuccessMessage] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [errors, setErrors] = useState({});
	const [showPassword, setShowPassword] = useState(false);

	const { loading, error, isAuthenticated } = useSelector(
		(state) => state.hospitalAuth,
	);

	// Extract the reset token from URL
	const { token } = useParams();
	useEffect(() => {
		if (token) {
			dispatch(hospitalVerifyEmail(token))
				.then(() => setSuccessMessage('Email verified successfully.'))
				.catch(() => setSuccessMessage('Verification failed.'));
		}
	}, [token, dispatch]);

	const verification = async (e) => {
		try {
			const response = await dispatch(hospitalVerifyEmail(token));
			if (response.status === 200) {
				setSuccessMessage('Email verified successfully.');
				navigate(APP_URLS.hospitalVerification);
			}
		} catch {
			console.log('Email verification failed. Try again.');
		}
	};
	verification();

	const resetCaptcha = () => {
		if (captchaRef.current) {
			captchaRef.current.reset();
			setCaptchaValue(null);
		}
	};

	const [formData, setFormData] = useState({
		email: '',
		password: '',
	});

	const {
		googleUserInfo,
		loading: googleLoading,
		handleGoogleSignIn,
	} = useGoogleAuth(dispatch, toast);

	// Already authenticated check
	useEffect(() => {
		let isMounted = true;

		if (isAuthenticated) {
			if (isMounted) {
				setSuccessMessage('You are logged in.');
				navigate(APP_URLS.hospitalProfile);
			}
		}

		return () => {
			isMounted = false;
		};
	}, [isAuthenticated]);

	const handleChange = async (e) => {
		const { name, value } = e.target;
		const updatedFormData = { ...formData, [name]: value };

		setFormData(updatedFormData);

		try {
			await validationSchema.validateAt(name, updatedFormData);
			setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
		} catch (error) {
			setErrors((prevErrors) => ({ ...prevErrors, [name]: error.message }));
		}
	};

	const handleCaptchaChange = (token) => {
		setCaptchaValue(token);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		// Check CAPTCHA completion
		if (!captchaValue) {
			showToast(toast, 'error', 'Please complete the CAPTCHA');
			return;
		}

		const isVerified = await verifyRecaptcha(captchaValue, setCaptchaError);
		if (!isVerified) {
			resetCaptcha();
			return;
		}

		try {
			// Validate form fields
			await validationSchema.validate(formData, { abortEarly: false });
			// Dispatch login action
			const loginResponse = await dispatch(
				loginHospital({ email: formData.email, password: formData.password }),
			);

			if (!loginResponse || loginResponse.status !== 200) {
				// Handle unexpected success statuses
				showToast(
					toast,
					'error',
					response.data?.message || 'Unexpected response from the server.',
				);
			}
		} catch (error) {
			// Handle validation errors (from Yup)
			if (error.name === 'ValidationError') {
				const validationErrors = {};
				error.inner.forEach((err) => {
					validationErrors[err.path] = err.message;
				});
				setErrors(validationErrors);
				showToast(toast, 'error', 'Please fix the highlighted errors.');
			}

			// Handle API errors
			if (error.response?.status === 400) {
				showToast(
					toast,
					'error',
					error.response?.data?.message ||
						'Validation error. Please check your input.',
				);
			} else if (error.response?.status === 401) {
				showToast(
					toast,
					'error',
					error.response?.data?.message || 'Invalid email or password.',
				);
			} else {
				// Handle unexpected errors
				showToast(
					toast,
					'error',
					error.message || 'An unexpected error occurred. Please try again.',
				);
			}
		}
		resetCaptcha();
	};

	return (
		<form onSubmit={handleSubmit} className={css(styles.form)}>
			{successMessage && <h3>{successMessage}</h3>}
			<motion.div
				className={css(styles.inputGroup)}
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3, delay: 0.1 }}
			>
				<input
					type="email"
					name="email"
					placeholder="Email"
					value={formData.email}
					onChange={handleChange}
					className={css(styles.input)}
					aria-describedby="emailError"
				/>
				{errors.email && (
					<p id="emailError" className={css(styles.error)}>
						{errors.email}
					</p>
				)}
			</motion.div>
			<motion.div
				className={css(styles.inputGroup)}
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3, delay: 0.2 }}
			>
				<div className={css(styles.passwordInputWrapper)}>
					<input
						type={showPassword ? 'text' : 'password'}
						name="password"
						placeholder="Password"
						value={formData.password}
						onChange={handleChange}
						aria-describedby="passwordError"
						className={css(styles.input, styles.passwordInput)}
					/>
					<button
						type="button"
						onClick={() => setShowPassword((prev) => !prev)}
						className={css(styles.passwordToggle)}
					>
						{showPassword ? (
							<EyeOff className={css(styles.eyeIcon)} />
						) : (
							<Eye className={css(styles.eyeIcon)} />
						)}
					</button>
				</div>
				{errors.password && (
					<p id="passwordError" className={css(styles.error)}>
						{errors.password}
					</p>
				)}
			</motion.div>
			<motion.div
				className={css(styles.captcha)}
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3, delay: 0.3 }}
			>
				<ReCAPTCHA
					ref={captchaRef}
					sitekey={process.env.GOOGLE_RECAPTCH_SITE_KEY}
					onChange={handleCaptchaChange}
				/>
				{captchaError && (
					<p style={{ color: 'red', marginTop: '5px' }}>{captchaError}</p>
				)}
			</motion.div>
			<motion.button
				type="submit"
				className={css(styles.button)}
				disabled={loading}
				whileHover={loading ? {} : { scale: 1.05 }}
				whileTap={loading ? {} : { scale: 0.95 }}
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3, delay: 0.4 }}
			>
				{loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
			</motion.button>
			<Divider />
			<motion.div
				whileHover={{ scale: 1.05 }}
				whileTap={{ scale: 0.95 }}
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3, delay: 0.4 }}
			>
				<GoogleLogin
					style={{ marginRight: '0.5rem', fontSize: '1.5rem' }}
					onSuccess={handleGoogleSignIn}
				/>
			</motion.div>
			<motion.button
				type="button"
				onClick={() => navigate(APP_URLS.hospitalForgotPassword)}
				className={css(styles.forgotPasswordText)}
				whileHover={{ scale: 1.05 }}
				whileTap={{ scale: 0.95 }}
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3, delay: 0.5 }}
			>
				Forgot Password?
			</motion.button>
		</form>
	);
};

const styles = StyleSheet.create({
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
	},
	inputGroup: {
		position: 'relative',
		marginBottom: '1rem',
	},
	captcha: {
		display: 'flex',
		justifyContent: 'start',
		marginBottom: '1rem',
	},
	input: {
		width: '100%',
		padding: '0.75rem',
		borderRadius: '8px',
		border: '1px solid #e2e8f0',
		fontSize: '1rem',
		transition: 'border-color 0.3s',
		':focus': {
			outline: 'none',
			borderColor: '#2783F7',
			boxShadow: '0 0 0 3px rgba(39, 131, 247, 0.1)',
		},
	},
	passwordInputWrapper: {
		position: 'relative',
		width: '100%',
	},
	passwordInput: {
		paddingRight: '40px',
	},
	passwordToggle: {
		position: 'absolute',
		right: '10px',
		top: '50%',
		transform: 'translateY(-50%)',
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		color: '#4a5568',
	},
	eyeIcon: {
		color: 'black',
		width: '1.15rem',
		height: '1.15rem',
	},
	button: {
		backgroundColor: '#2783F7',
		color: 'white',
		padding: '0.75rem',
		borderRadius: '8px',
		border: 'none',
		fontSize: '1rem',
		fontWeight: '600',
		cursor: 'pointer',
		transition: 'background-color 0.3s',
		':hover': {
			backgroundColor: '#1c5fa8',
		},
		':disabled': {
			backgroundColor: '#a0aec0',
			cursor: 'not-allowed',
		},
	},
	forgotPasswordText: {
		all: 'unset',
		color: '#2783F7',
		textAlign: 'center',
		fontSize: '0.875rem',
		fontWeight: '600',
		cursor: 'pointer',
		textDecoration: 'none',
		':hover': {
			textDecoration: 'underline',
		},
	},
	error: {
		color: '#e53e3e',
		fontSize: '0.875rem',
		marginTop: '0.5rem',
		marginBottom: '1rem',
	},
});

export default HospitalLogin;
